import { useFieldArray, useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";

import { useOpenBreakoutSession } from "../api/breakout-session";
import {
  CreateBreakoutSessionsFormData,
  createBreakoutSessionsSchema,
} from "../types/create-breakout-sessions.schema";

import { BreakoutSessionElement } from "./breakout-session.element";
import { NumberOfRoomsElement } from "./number-of-rooms.element";
import { RandomiseBreakoutSessionElement } from "./randomise-breakout-session.element";
import { ResetBreakoutSessionsElement } from "./reset-breakout-sessions.element";
import { TimerElement } from "./timer.element";

export function CreateBreakoutSessionForm() {
  const queryClient = useQueryClient();
  const form = useForm<CreateBreakoutSessionsFormData>({
    resolver: zodResolver(createBreakoutSessionsSchema),
    defaultValues: {
      breakoutSessions: [
        {
          name: `Breakout Room 1`,
          participants: [],
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "breakoutSessions",
  });

  const { mutateAsync: openBreakoutSession } = useOpenBreakoutSession({
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ["breakout-sessions", sessionId],
      });
    },
  });
  const sessionId = useVidyoStore((store) => store.sessionId);

  const handleCreateBreakoutSession = async (
    data: CreateBreakoutSessionsFormData,
  ) => {
    await openBreakoutSession({
      sessionId: sessionId,
      body: {
        timer: data.timer,
        rooms: data.breakoutSessions.map((breakoutSession) => ({
          name: breakoutSession.name,
          participants: breakoutSession.participants.map((participant) => ({
            participantId: participant.participantId,
            entityId: participant.entityId,
          })),
        })),
      },
    });
  };

  const numberOfRooms = fields.length;

  const handleRoomChange = (newRoomCount: number) => {
    if (newRoomCount > numberOfRooms) {
      // Add rooms
      for (let i = numberOfRooms; i < newRoomCount; i++) {
        append(
          {
            name: `Breakout Room ${i + 1}`,
            participants: [],
          },
          { shouldFocus: false },
        );
      }
    } else if (newRoomCount < numberOfRooms) {
      // Remove rooms
      for (let i = numberOfRooms; i > newRoomCount; i--) {
        remove(i - 1);
      }
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleCreateBreakoutSession)}
        className="flex h-full flex-col"
      >
        <div className="mb-24 px-4">
          <div className="my-4 flex items-center gap-4">
            <NumberOfRoomsElement
              onRoomNumberChange={handleRoomChange}
              value={numberOfRooms}
            />
            <div className="ml-auto flex flex-row gap-4">
              <TimerElement />
              <ResetBreakoutSessionsElement />
              <RandomiseBreakoutSessionElement />
            </div>
          </div>
          <div className="space-y-4">
            {fields.map((field, index) => (
              <BreakoutSessionElement key={field.id} index={index} />
            ))}
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 mt-4 rounded-b-md border-t border-border px-4 py-4 backdrop-blur-md">
          <Button
            type="submit"
            className="w-full"
            isLoading={form.formState.isSubmitting}
            isAnimated
          >
            Open Rooms
          </Button>
        </div>
      </form>
    </Form>
  );
}
